import React from "react";
import diff from "json-patch-gen";
import { transformMillisecToSec } from "component-library";
import clone from "clone";

import { GlobalEventTypes } from "contexts/GlobalContext";
import { IGlobalContextProps } from "shared/contexts/GlobalContext";
import { JsonPatchOperation } from "shared/utils/http/json-patch-generator";
import mediaService from "mediaModules/media/services/MediaService";
import {
  MediaAsset,
  MediaAssetType,
  MediaObject,
  MediaStorageSpace,
  TranscodeStatus,
  MediaMetadata,
} from "mediaModules/media/models";
import { captureExceptionEvent } from "shared-services";

interface IWithCombinedMediaDataState {
  mediaAssets: MediaAsset[];
  storageSpaceByType: Record<MediaAssetType, MediaStorageSpace>;
  timeoutIds: ReturnType<typeof setTimeout>[];
  isMediaMetadataLoading: boolean;
  areMediaObjectsLoading: boolean;
  isPatching: boolean;
}

export interface IWithCombinedMediaDataInjectedProps {
  mediaAssets: MediaAsset[];
  storageSpaceByType: Record<MediaAssetType, MediaStorageSpace>;
  isMediaMetadataLoading: boolean;
  areMediaObjectsLoading: boolean;
  removeMediaAssets: (mediaAssets: MediaAsset[]) => Promise<void>;
  hideMediaAssets: (
    mediaAssets: MediaAsset[],
    displayLimitReachedCallback: () => void
  ) => Promise<void>;
  changeMediaAssetsOrder: (
    id: string,
    oldSortOrder: number,
    newSortOrder: number
  ) => Promise<void>;
  isPatching: boolean;
}

const PROCESSING_MEDIA_REQUEST_TIMEOUT = 5000;

export default function withCombinedMediaData<P extends IGlobalContextProps>(
  WrappedComponent:
    | React.ComponentClass<P>
    | React.FC<React.PropsWithChildren<P>>
): typeof React.Component {
  return class extends React.Component<P, IWithCombinedMediaDataState> {
    public readonly state: Readonly<IWithCombinedMediaDataState> = {
      mediaAssets: [],
      storageSpaceByType: {
        [MediaAssetType.Audio]: {
          allowedStorageSpace: {
            maxAllowedStoreInSec: 0,
            maxAllowedDisplayInSec: 0,
          },
          usedStorageSpace: {
            usedDisplayValueInSec: 0,
            usedStoreValueInSec: 0,
          },
        },
        [MediaAssetType.Video]: {
          allowedStorageSpace: {
            maxAllowedStoreInSec: 0,
            maxAllowedDisplayInSec: 0,
          },
          usedStorageSpace: {
            usedDisplayValueInSec: 0,
            usedStoreValueInSec: 0,
          },
        },
      },
      isMediaMetadataLoading: true,
      areMediaObjectsLoading: true,
      timeoutIds: [],
      isPatching: false,
    };
    private mounted: boolean;

    public async componentDidMount() {
      this.mounted = true;
      await this.getAllowedStorageSpace();
      await this.getMediaMetadata();
      this.getMediaAssets();
    }

    public componentWillUnmount() {
      this.mounted = false;
      this.state.timeoutIds.forEach((id) => clearTimeout(id));
    }

    private async getAllowedStorageSpace() {
      try {
        const storageSpaceByType = await mediaService.getAllowedStorageSpace();

        if (this.mounted) {
          this.setState({
            storageSpaceByType: {
              [MediaAssetType.Audio]: {
                ...this.state.storageSpaceByType[MediaAssetType.Audio],
                allowedStorageSpace: storageSpaceByType[MediaAssetType.Audio],
              },
              [MediaAssetType.Video]: {
                ...this.state.storageSpaceByType[MediaAssetType.Video],
                allowedStorageSpace: storageSpaceByType[MediaAssetType.Video],
              },
            },
          });
        }
      } catch (error) {
        captureExceptionEvent(error);
      }
    }

    private getMediaMetadata = async () => {
      try {
        const mediaMetadataItems = await mediaService.getMediaMetadata();
        const mediaAssetsPreload = mediaMetadataItems.map((item) =>
          mediaService.createMediaAssetFromMeta(item)
        );

        if (this.mounted) {
          this.setState({
            mediaAssets: mediaAssetsPreload,
            isMediaMetadataLoading: false,
          });
          this.getMediaAssets(mediaAssetsPreload);
        }
      } catch (error) {
        captureExceptionEvent(error);
      }
    };

    private getMediaAssets = (mediaAssets?: MediaAsset[]) => {
      const assetsToUse = mediaAssets || this.state.mediaAssets;

      if (assetsToUse.length) {
        for (const { mediaInfoId } of assetsToUse) {
          void this.pollMediaObject(mediaInfoId, undefined, assetsToUse);
        }
      } else if (this.mounted) {
        this.setState({ areMediaObjectsLoading: false });
      }
    };

    private pollMediaObject = async (
      mediaInfoId: string,
      timeoutId?: ReturnType<typeof setTimeout>,
      mediaAssets?: MediaAsset[]
    ) => {
      const assetsToSearch = mediaAssets || this.state.mediaAssets;
      const mediaMetadataToPoll = assetsToSearch.find(
        ({ mediaInfoId: id }) => id === mediaInfoId
      );

      if (!mediaMetadataToPoll) {
        return;
      }

      const mediaObject = await mediaService.getMediaObject(
        mediaInfoId,
        this.closePopup
      );

      if (this.isProcessingItem(mediaObject.transcodeStatus) && this.mounted) {
        const newTimeoutId = setTimeout(async () => {
          await this.pollMediaObject(mediaInfoId, newTimeoutId);
        }, PROCESSING_MEDIA_REQUEST_TIMEOUT);
        this.setState({
          timeoutIds: [
            ...this.state.timeoutIds.filter((id) => id !== timeoutId),
            newTimeoutId,
          ],
        });
      }

      this.updateMediaAssets(mediaObject);
    };

    private updateMediaAssets = (mediaObject: MediaObject) => {
      if (!this.mounted) {
        return;
      }

      const updatedMediaAssets = this.state.mediaAssets.map((mediaAsset) =>
        mediaAsset.mediaInfoId === mediaObject.id
          ? mediaService.mapUpdatedMediaObject(mediaAsset, mediaObject)
          : mediaAsset
      );
      const usedStorageSpaceByType =
        this.getUsedStorageSpaceByType(updatedMediaAssets);
      const areMediaObjectsLoading = updatedMediaAssets.some(
        (item) => item.isMediaObjectLoading
      );
      this.setState({
        mediaAssets: updatedMediaAssets,
        storageSpaceByType: {
          [MediaAssetType.Audio]: {
            ...this.state.storageSpaceByType[MediaAssetType.Audio],
            usedStorageSpace: usedStorageSpaceByType[MediaAssetType.Audio],
          },
          [MediaAssetType.Video]: {
            ...this.state.storageSpaceByType[MediaAssetType.Video],
            usedStorageSpace: usedStorageSpaceByType[MediaAssetType.Video],
          },
        },
        areMediaObjectsLoading,
      });
    };

    private closePopup = () =>
      this.props.globalContext.notifyListener(
        GlobalEventTypes.closeAllGlobalAlert
      );

    private isProcessingItem = (transcodeStatus: TranscodeStatus) =>
      transcodeStatus === TranscodeStatus.Awaiting ||
      transcodeStatus === TranscodeStatus.Processing;

    private removeMediaAssets = (assetsToRemove: MediaAsset[]) => {
      this.setState({ isPatching: true });
      try {
        const mediaAssets = this.state.mediaAssets.filter(
          (mediaAsset) => !assetsToRemove.some(({ id }) => mediaAsset.id === id)
        );
        const usedStorageSpaceByType =
          this.getUsedStorageSpaceByType(mediaAssets);
        this.setState(
          {
            mediaAssets,
            storageSpaceByType: {
              [MediaAssetType.Audio]: {
                ...this.state.storageSpaceByType[MediaAssetType.Audio],
                usedStorageSpace: usedStorageSpaceByType[MediaAssetType.Audio],
              },
              [MediaAssetType.Video]: {
                ...this.state.storageSpaceByType[MediaAssetType.Video],
                usedStorageSpace: usedStorageSpaceByType[MediaAssetType.Video],
              },
            },
          },
          async () =>
            await this.commitRemovingMediaAssets(assetsToRemove, mediaAssets)
        );
      } catch (error) {
        captureExceptionEvent(error);
        this.setState({ isPatching: false });
      }

      this.closePopup();
    };

    private commitRemovingMediaAssets = async (
      assetsToRemove: MediaAsset[],
      mediaAssets: MediaAsset[]
    ) => {
      for (const { id, mediaInfoId } of assetsToRemove) {
        try {
          await mediaService.deleteMediaAsset(id, mediaInfoId, this.closePopup);
        } catch (error) {
          captureExceptionEvent(error);
        }
      }

      try {
        const metadata = await mediaService.getMediaMetadata();
        const mediaAssetsUpdated = await mediaService.updateMediaAssetsMetadata(
          metadata,
          mediaAssets
        );
        const usedStorageSpaceByType =
          this.getUsedStorageSpaceByType(mediaAssetsUpdated);
        this.setState({
          mediaAssets: mediaAssetsUpdated,
          storageSpaceByType: {
            [MediaAssetType.Audio]: {
              ...this.state.storageSpaceByType[MediaAssetType.Audio],
              usedStorageSpace: usedStorageSpaceByType[MediaAssetType.Audio],
            },
            [MediaAssetType.Video]: {
              ...this.state.storageSpaceByType[MediaAssetType.Video],
              usedStorageSpace: usedStorageSpaceByType[MediaAssetType.Video],
            },
          },
        });
      } catch (error) {
        captureExceptionEvent(error);
      }

      this.setState({ isPatching: false });
    };

    private unhidePreCheck = (mediaAssetsToUpdate: MediaAsset[]) => {
      const { storageSpaceByType, mediaAssets } = this.state;

      if (
        mediaAssetsToUpdate.length === 1 &&
        mediaAssets.length === 1 &&
        mediaAssets[0].id === mediaAssetsToUpdate[0].id
      ) {
        return {
          allItemsAreHidden: mediaAssets[0].visible === false,
          visibilityCanBeChanged: true,
        };
      }

      // Group assets by type to check limits per media type
      const assetsByType = mediaAssetsToUpdate.reduce(
        (acc, asset) => {
          const type = asset.mediaType;

          if (!acc[type]) {
            acc[type] = [];
          }

          acc[type].push(asset);

          return acc;
        },
        {} as Record<MediaAssetType, MediaAsset[]>
      );

      let allItemsAreHidden = true;
      let visibilityCanBeChanged = true;

      // Check each media type separately
      for (const [mediaType, assets] of Object.entries(assetsByType) as [
        MediaAssetType,
        MediaAsset[],
      ][]) {
        const { usedStorageSpace, allowedStorageSpace } =
          storageSpaceByType[mediaType];

        const { typeItemsHidden, totalItemsDurationInSec } = assets.reduce(
          (acc, { visible, durationInMs }: MediaAsset) => ({
            typeItemsHidden: acc.typeItemsHidden && !visible,
            totalItemsDurationInSec:
              acc.totalItemsDurationInSec +
              transformMillisecToSec(durationInMs),
          }),
          { typeItemsHidden: true, totalItemsDurationInSec: 0 }
        );

        allItemsAreHidden = allItemsAreHidden && typeItemsHidden;

        const durationInSecAfterUnhide =
          totalItemsDurationInSec + usedStorageSpace.usedDisplayValueInSec;

        if (
          typeItemsHidden &&
          durationInSecAfterUnhide > allowedStorageSpace.maxAllowedDisplayInSec
        ) {
          visibilityCanBeChanged = false;
        }
      }

      return { allItemsAreHidden, visibilityCanBeChanged };
    };

    private hideMediaAssets = (
      mediaAssets: MediaAsset[],
      displayLimitReachedCallback: () => void
    ) => {
      this.setState({ isPatching: true });
      const { allItemsAreHidden, visibilityCanBeChanged } =
        this.unhidePreCheck(mediaAssets);

      if (allItemsAreHidden && !visibilityCanBeChanged) {
        this.setState({ isPatching: false });
        displayLimitReachedCallback();
      } else {
        try {
          const patchesToApply: {
            difference: JsonPatchOperation[];
            id: string;
          }[] = [];

          for (const { id, visible } of mediaAssets) {
            const difference = diff(
              { visible: String(visible) },
              { visible: String(allItemsAreHidden) }
            );

            if (difference.length) {
              patchesToApply.push({ difference, id });
            }
          }

          if (patchesToApply.length) {
            const nextAssets = this.getHideMediaAssetsResult(
              this.state.mediaAssets,
              patchesToApply.map(({ id }) => id),
              allItemsAreHidden
            );
            const usedStorageSpaceByType =
              this.getUsedStorageSpaceByType(nextAssets);
            this.setState(
              {
                mediaAssets: nextAssets,
                storageSpaceByType: {
                  [MediaAssetType.Audio]: {
                    ...this.state.storageSpaceByType[MediaAssetType.Audio],
                    usedStorageSpace:
                      usedStorageSpaceByType[MediaAssetType.Audio],
                  },
                  [MediaAssetType.Video]: {
                    ...this.state.storageSpaceByType[MediaAssetType.Video],
                    usedStorageSpace:
                      usedStorageSpaceByType[MediaAssetType.Video],
                  },
                },
              },
              async () =>
                await this.commitUpdatingMediaMetadata(
                  patchesToApply,
                  nextAssets
                )
            );
          } else {
            this.setState({ isPatching: false });
          }
        } catch (error) {
          captureExceptionEvent(error);
          this.setState({ isPatching: false });
        }
      }
    };

    private getHideMediaAssetsResult = (
      mediaAssets: MediaAsset[],
      ids: string[],
      visible: boolean
    ): MediaAsset[] => {
      if (!mediaAssets.length) {
        return [];
      }

      const newMediaAssets: MediaAsset[] = clone(mediaAssets);
      for (const id of ids) {
        const index = newMediaAssets.findIndex((asset) => asset.id === id);
        newMediaAssets[index].visible = visible;
      }

      return newMediaAssets;
    };

    private changeMediaAssetsOrder = (
      id: string,
      oldIndex: number,
      newIndex: number
    ) => {
      this.setState({ isPatching: true });
      try {
        const orderedAssets = [...this.state.mediaAssets].sort(
          (first, second) => {
            if (first.mergedSortOrder !== second.mergedSortOrder) {
              return first.mergedSortOrder - second.mergedSortOrder;
            }

            return first.title.localeCompare(second.title);
          }
        );
        const assetToMove = orderedAssets[oldIndex];

        if (!assetToMove) {
          this.setState({ isPatching: false });

          return;
        }

        const reorderedAssets = [...orderedAssets];
        reorderedAssets.splice(oldIndex, 1);
        reorderedAssets.splice(newIndex, 0, assetToMove);

        const patchesToApply: {
          difference: JsonPatchOperation[];
          id: string;
        }[] = [];
        const updatedAssets = reorderedAssets.map((asset, index) => {
          if (asset.mergedSortOrder === index) {
            return asset;
          }

          const difference = diff(
            { mergedSortOrder: String(asset.mergedSortOrder) },
            { mergedSortOrder: String(index) }
          );

          if (difference.length) {
            patchesToApply.push({ difference, id: asset.id });
          }

          return { ...asset, mergedSortOrder: index };
        });

        if (patchesToApply.length) {
          this.setState(
            { mediaAssets: updatedAssets },
            async () =>
              await this.commitUpdatingMediaMetadata(
                patchesToApply,
                updatedAssets
              )
          );
        } else {
          this.setState({ isPatching: false });
        }
      } catch (error) {
        captureExceptionEvent(error);
        this.setState({ isPatching: false });
      }
    };

    private commitUpdatingMediaMetadata = async (
      differences: { difference: JsonPatchOperation[]; id: string }[],
      mediaAssets: MediaAsset[]
    ) => {
      let metadata: MediaMetadata[] = [];
      for (const { difference, id } of differences) {
        try {
          metadata = await mediaService.updateMediaMetadata(id, difference);
        } catch (error) {
          captureExceptionEvent(error);
          try {
            metadata = await mediaService.getMediaMetadata();
          } catch (err) {
            captureExceptionEvent(err);
          }
        }
      }

      if (metadata.length) {
        const mediaAssetsUpdated = await mediaService.updateMediaAssetsMetadata(
          metadata,
          mediaAssets
        );
        const usedStorageSpaceByType =
          this.getUsedStorageSpaceByType(mediaAssetsUpdated);
        this.setState({
          mediaAssets: mediaAssetsUpdated,
          storageSpaceByType: {
            [MediaAssetType.Audio]: {
              ...this.state.storageSpaceByType[MediaAssetType.Audio],
              usedStorageSpace: usedStorageSpaceByType[MediaAssetType.Audio],
            },
            [MediaAssetType.Video]: {
              ...this.state.storageSpaceByType[MediaAssetType.Video],
              usedStorageSpace: usedStorageSpaceByType[MediaAssetType.Video],
            },
          },
        });
      } else {
        captureExceptionEvent("Error: Patching process has been failed");
      }

      this.setState({ isPatching: false });
    };

    private getUsedStorageSpace = (mediaAssets: MediaAsset[]) => {
      const visibleMediaAssets = mediaAssets.filter(({ visible }) => visible);
      const reducer = (acc: number, { durationInMs }: MediaAsset) =>
        acc + transformMillisecToSec(durationInMs);

      return {
        usedDisplayValueInSec: visibleMediaAssets.reduce(reducer, 0),
        usedStoreValueInSec: mediaAssets.reduce(reducer, 0),
      };
    };

    private getUsedStorageSpaceByType = (mediaAssets: MediaAsset[]) => {
      const audioAssets = mediaAssets.filter(
        (asset) => asset.mediaType === MediaAssetType.Audio
      );
      const videoAssets = mediaAssets.filter(
        (asset) => asset.mediaType === MediaAssetType.Video
      );

      return {
        [MediaAssetType.Audio]: this.getUsedStorageSpace(audioAssets),
        [MediaAssetType.Video]: this.getUsedStorageSpace(videoAssets),
      };
    };

    public render() {
      const {
        mediaAssets,
        storageSpaceByType,
        isMediaMetadataLoading,
        areMediaObjectsLoading,
        isPatching,
      } = this.state;

      return (
        <WrappedComponent
          mediaAssets={mediaAssets}
          storageSpaceByType={storageSpaceByType}
          isMediaMetadataLoading={isMediaMetadataLoading}
          areMediaObjectsLoading={areMediaObjectsLoading}
          removeMediaAssets={this.removeMediaAssets}
          hideMediaAssets={this.hideMediaAssets}
          changeMediaAssetsOrder={this.changeMediaAssetsOrder}
          isPatching={isPatching}
          {...this.props}
        />
      );
    }
  };
}
